import {
  isUserVerifiedAndIsSavingsProductsProvider,
  urlSearchParamsToJson,
} from '@app/finxone-web-frontend/app/Modules/zones/utils';
import { SetCurrentCardTransactionDetail } from '@app/finxone-web-frontend/app/lib/actions/card-management.action';
import { Currency } from '@app/finxone-web-frontend/app/lib/dtos/currencies.dto';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import { PageContextState } from '@app/finxone-web-frontend/app/lib/state/page-context.state';
import { SavingsProductsState } from '@app/finxone-web-frontend/app/lib/state/savings-products.state';
import { currentCardTransactionDetailSkeleton } from '@finxone-platform/shared/default-templates';
import { selectedNotificationSkeleton } from '@finxone-platform/shared/default-templates';
import { resolveAccountIdentifierFields } from '@app/finxone-web-frontend/app/lib/utils/account-identifier.utils';
import { addDashesToSortCode } from '@app/finxone-web-frontend/app/lib/utils/template/string.utils';
import { flattenFormStateValues, flattenObject } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { FormActionTypeEnum, FormID } from '@finxone-platform/form-action';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  AccountStatusType,
  CardStatusType,
  CardTransactionDetail,
  DepositDetailsType,
  PaymentProvider,
  ProfileStateModel,
  SpendingBudget,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import {
  combineLatest,
  delay,
  distinctUntilChanged,
  first,
  from,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { UAParser } from 'ua-parser-js';
import { GetAccount } from '../../actions/account.action';
import { GetBeneficiary } from '../../actions/beneficiary.action';
import { GetCards } from '../../actions/card.actions';
import { GetDepositAccountInfo } from '../../actions/deposit-account-info.action';
import {
  FetchNotifications,
  ProcessNotification,
  SetSelectedNotification,
} from '../../actions/notification.action';
import { GetOrganisation } from '../../actions/organisation.action';
import { GetPaymentSummaryCounters } from '../../actions/payment-summary-counters.action';
import { GetCancellablePaymentsList } from '../../actions/payment.action';
import {
  GetAccountRequests,
  GetAvailableSavingsProducts,
  GetNominatedAccountDetails,
  GetNominatedAccountStatus,
  GetSavingAccount,
  GetSupportedPispInstitutions,
} from '../../actions/savings-account.action';
import { FetchTotalWealth } from '../../actions/total-wealth.action';
import { GetProfile } from '../../actions/user-profile.action';
import { Country } from '../../dtos/countries.dto';
import { GetBeneficiaryResponse, TransactionStateType } from '../../services/account-service/account.type';
import { AccountOpenRequestType } from '../../services/savings-account-service/savings-account.type';
import { AccountState, AccountStateModel } from '../../state/account.state';
import { ApiActions, ApiLoadingState } from '../../state/api-loading.state';
import { AppDataState, AppDataStateModel } from '../../state/app-data.state';
import { BeneficiaryState, BeneficiaryStateModel } from '../../state/beneficiary.state';
import { BulkPaymentListState } from '../../state/bulk-payment.state';
import { CardState, CardStateModel } from '../../state/card.state';
import { CopResponseState, CopResponseStateModel } from '../../state/cop-response.state';
import {
  DepositAccountInfoState,
  DepositAccountInfoStateModel,
} from '../../state/deposit-account-info.state';
import { FormActionState } from '../../state/form-submision.state';
import {
  NotificationState,
  NotificationStateModel,
  PaymentRequestSummary,
} from '../../state/notifications.state';
import { OrganisationState, OrganisationStateModel } from '../../state/organisation.state';
import { PaymentSummaryCounterState } from '../../state/payment-summary-counters.state';
import { PaymentState } from '../../state/payment.state';
import { ProgressBarState } from '../../state/progress-bar.state';
import { ProjectSettingsState } from '../../state/project-settings.state';
import { RecurringPaymentsState, RecurringPaymentsStateModel } from '../../state/recurring-payments.state';
import { SavingsAccountState, SavingsAccountStateModel } from '../../state/savings-account.state';
import { TotalWealthState, TotalWealthStateModel } from '../../state/total-wealth.state';
import { TransactionState } from '../../state/transaction.state';
import { ProfileState } from '../../state/user-profile.state';
import { ConfigService } from '../../services/config-service/config-service.service';
import { NotificationService } from '../../services/notification-service/notification-service.service';
import { hasUnreadNotifications } from './handlebar-helpers/count-when-field-equals.helper';
import { getProviderSpecificPageLimitForSavingsAccounts } from './payment-provider.utils';
import {
  dispatchActiveSavingsAccountDetailsFetch,
  getPendingJointAccountRequests,
  isPispInstitutionDisplayEnabled,
} from './model-builder-helper.utils';

export function filterIfHandlebarNotRendered(content: string) {
  const containDoubleBraces = /{{[^{}]*}}/.test(content);
  return containDoubleBraces ? '' : content;
}

function isASavingsProvider(provider: PaymentProvider | string | undefined): boolean {
  return (
    provider === PaymentProvider.THISBANK ||
    provider === PaymentProvider.SAASCADA ||
    provider === PaymentProvider.UNIVERSAL_SANDBOX
  );
}

export type GenericHandleBarModel = {
  appData: AppDataStateModel;
  profile: ProfileStateModel;
  cards: CardStateModel;
  savingsAccounts: SavingsAccountStateModel;
  accounts: AccountStateModel;
  availableBalance:
    | string
    | {
        currency: string;
        amount: string;
      }[];
  accountBalance:
    | string
    | {
        currency: string;
        amount: string;
      }[];
  currency: string | { currency: string }[];
  sortCode: string;
  accountNumber: string;
  iban: string;
  ledgerNumber: string;
  bic: string;
  selectedBeneficiary: GetBeneficiaryResponse | object;
  paymentRequest: Record<string, string> | undefined;
  baseFormStateData: Record<string, string> | undefined;
  beneficiaries: BeneficiaryStateModel;
  beneficiaryDetails: any;
  copResponse: CopResponseStateModel;
  notification: NotificationStateModel;
  hasUnreadNotifications: boolean;
  accountCreatedDate: string;
  currencyFullName: string;
  selectedAccountStatus?: string;
  pageContext: Record<string, string>;
  intlBeneficiaryAdd: Record<string, string> | undefined;
  countryName: string;
  organisationData: OrganisationStateModel;
  customQuestionFormData: Record<string, string>;
  formSubmissionFormData: Record<string, string> | undefined;
  beneficiaryDetail: GetBeneficiaryResponse | undefined;
  paymentRequestSummary: PaymentRequestSummary | undefined;
  profileBankStatementFormState: Record<string, string> | undefined;
  urlParamValue: Record<string, string>;
  transaction: TransactionStateType;
  currentCardIsOnXPay?: boolean;
  sepaFormState: Record<string, string>;
  userProfileUpdateForm: Record<string, string>;
  depositAccountInfo: DepositAccountInfoStateModel;
  allFormValues?: Record<string, any> | undefined;
  nominatedAccountStatus: string | undefined;
  totalWealth: TotalWealthStateModel;
  accountRequests: AccountOpenRequestType[] | undefined;
  spendingBudget: SpendingBudget | undefined;
  rfi: { hasOpen: boolean; lastRfiId?: string };
  recurringPayments: RecurringPaymentsStateModel;
};

export function buildHandleBarModel(
  store: Store,
  template: string,
  countriesCurrenciesService: CountriesCurrenciesService,
  iconCacheService: IconCacheService,
  accountService?: AccountService,
  attributes?: UiZoneWidgetAttributeConfig,
  isSkeletonPreview?: boolean,
  workflowFormsService?: WorkflowFormsService,
  noAutoRefreshStateData?: boolean,
  configService?: ConfigService,
): Observable<GenericHandleBarModel> {
  if (template && noAutoRefreshStateData !== true)
    findAndRefreshRequiredData(store, template, attributes, isSkeletonPreview);

  const selectedBeneficiary$ = getBeneficiaryDetails(store);
  const paymentRequest$ = paymentRequestData(store);
  const appData$ = store.select(AppDataState.getAppData);
  const profile$ = store.select(ProfileState.getProfile);
  const cards$ = store.select(CardState.getCards);
  const savingsAccounts$ = store.select(SavingsAccountState.getAccounts);
  const accounts$ = store.select(AccountState.getAccounts);
  const depositAccountInfo$ = store.select(DepositAccountInfoState.getDepositAccountInfo);
  const beneficiaries$ = store.select(BeneficiaryState.getBeneficiary);
  const addBeneficiaryForm$ = fetchFormDataById(store, FormActionTypeEnum.ADD_BENEFICIARY);
  const editBeneficiaryForm$ = fetchFormDataById(store, FormActionTypeEnum.EDIT_BENEFICIARY);
  const copResponse$ = store.select(CopResponseState.getCopResponseState);
  const notification$ = store.select(NotificationState.getNotification);
  const currencies$ = countriesCurrenciesService.getCurrencies();
  const countries$ = countriesCurrenciesService.getCountries();
  const pageContext$ = store.select(PageContextState.getPageContextState);
  const intlBeneficiaryAdd$ = fetchFormDataById(store, FormActionTypeEnum.INTL_BENEFICIARY_ADD);
  const kycFormData = fetchFormDataById(store, FormActionTypeEnum.KYC_DETAILS);
  const profileFormData = fetchFormDataById(store, FormActionTypeEnum.EDIT_PROFILE_DETAILS);
  const organisationData$ = store.select(OrganisationState.getOrganisation);
  const customQuestionFormData = fetchFormDataById(store, FormActionTypeEnum.CUSTOM_QUESTION);
  const formSubmissionFormData$ = formSubmissionFormData(store);

  const selectedBeneficiaryFromBeneficiaryZone$ = getSelectedBeneficiaryDetail(store);
  const paymentRequestSummary$ = getPaymentRequestSummary(store);
  const baseFormStateData$ = baseFormStateDataData(store);
  const bulkPaymentSummary = store.select(BulkPaymentListState.getBulkPaymentSummary);
  const profileBankStatementFormState = formActionStateValueWithID(
    store,
    FormActionTypeEnum.PROFILE_BANK_STATEMENT,
  );
  const paymentSummaryCounters = store.select(PaymentSummaryCounterState.getPaymentSummaryCounters);
  const urlParamValue = getPageUrlParam();
  const transaction = store.select(TransactionState.getTransactions);
  const nominatedAccountStatus$ = refreshNominatedAccountStatus(store);
  const sepaFormState = formActionStateValueWithID(store, FormActionTypeEnum.SEPA_PAYMENT);
  const isTablet$ = from(Promise.resolve(UAParser().withFeatureCheck())).pipe(
    map((result) => result.device.type === 'tablet'),
  );
  const beneficiaryForm$ = workflowFormsService
    ? workflowFormsService.observeForm(FormID.BENEFICIARY_FORM)
    : of(undefined);
  const onboardingFlow$ = formActionStateValueWithID(store, FormID.ONBOARDING_FLOW);
  const userProfileUpdateForm$ = formActionStateValueWithID(store, FormID.UPDATE_USER_PROFILE);
  const openNewAccountForm$ = formActionStateValueWithID(store, FormID.OPEN_NEW_ACCOUNT_FORM);
  const maturityDecisionForm$ = formActionStateValueWithID(store, FormID.MATURITY_DECISION);
  const nominatedAccountDetails$ = store.select(SavingsAccountState.getNominatedAccountDetails);
  const apiLoadingState$ = store.select(ApiLoadingState.getApiLoadingState);

  //stores all forms values present in the workflow forms service's forms signal and form action state combined
  const allFormValues$ = getAllFormsValues(store, workflowFormsService);

  const totalWealth$ = store.select(TotalWealthState.getTotalWealthState);
  const accountRequests$ = refreshAccountRequests(store);
  const deviceInfo$ = of(new UAParser().getResult());
  const cancellablePayments$ = store.select(PaymentState.getCancellablePaymentsList);

  const pispInstitution$ = getPispInstitution(store, configService);

  const pendingJointAccountRequests$ = store.select(SavingsAccountState.getPendingJointAccountRequests);

  const recurringPayments$ = store.select(RecurringPaymentsState.getRecurringPayments);

  const isGocardlessCustomPaymentPagesEnabled =
    configService?.config?.goCardlessEnabledCustomPaymentPages ?? false;

  const pispDepositInitiateResponse$ = store.select(SavingsAccountState.getAccounts).pipe(
    map((state) => {
      return state.pispDepositInitiateResponse;
    }),
  );

  return combineLatest([
    // if a fetch was triggeed then this will emit to trigger rerender once fetched
    iconCacheService.iconLoaded$,
    appData$,
    profile$,
    cards$,
    savingsAccounts$,
    accounts$,
    depositAccountInfo$,
    selectedBeneficiary$,
    paymentRequest$,
    beneficiaries$,
    addBeneficiaryForm$,
    editBeneficiaryForm$,
    copResponse$,
    notification$,
    currencies$,
    countries$,
    pageContext$,
    intlBeneficiaryAdd$,
    organisationData$,
    kycFormData,
    profileFormData,
    customQuestionFormData,
    formSubmissionFormData$,
    selectedBeneficiaryFromBeneficiaryZone$,
    paymentRequestSummary$,
    profileBankStatementFormState,
    baseFormStateData$,
    bulkPaymentSummary,
    urlParamValue,
    transaction,
    paymentSummaryCounters,
    sepaFormState,
    isTablet$,
    beneficiaryForm$,
    onboardingFlow$,
    userProfileUpdateForm$,
    openNewAccountForm$,
    maturityDecisionForm$,
    nominatedAccountStatus$,
    nominatedAccountDetails$,
    allFormValues$,
    totalWealth$,
    accountRequests$,
    deviceInfo$,
    cancellablePayments$,
    apiLoadingState$,
    pispInstitution$,
    pispDepositInitiateResponse$,
    pendingJointAccountRequests$,
    recurringPayments$,
  ]).pipe(
    map(
      ([
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        iconLoaded,
        appData,
        profile,
        cards,
        savingsAccounts,
        accounts,
        depositAccountInfo,
        selectedBeneficiary,
        paymentRequest,
        beneficiaries,
        addBeneficiaryForm,
        editBeneficiaryForm,
        copResponse,
        notification,
        currencies,
        countries,
        pageContext,
        intlBeneficiaryAdd,
        organisationData,
        kycFormData,
        profileFormData,
        customQuestionFormData,
        formSubmissionFormData,
        selectedBeneficiaryFromBeneficiaryZone$,
        paymentRequestSummary$,
        profileBankStatementFormState,
        baseFormStateData$,
        bulkPaymentSummary,
        urlParamValue,
        transaction,
        paymentSummaryCounters,
        sepaFormState,
        isTablet,
        beneficiaryForm$,
        onboardingFlow$,
        userProfileUpdateForm,
        openNewAccountForm$,
        maturityDecisionForm$,
        nominatedAccountStatus,
        nominatedAccountDetails,
        allFormValues$,
        totalWealth,
        accountRequests,
        deviceInfo,
        cancellablePayments,
        apiLoadingState,
        pispInstitution,
        pispDepositInitiateResponse,
        pendingJointAccountRequests,
        recurringPayments,
      ]) => {
        const selectedAccountDetail = accounts.accounts?.find(
          (account) => account.accountId === accounts.currentAccountId,
        );
        const selectedSavingAccountDetail = savingsAccounts.accounts?.find(
          (account) => account.accountId === savingsAccounts.activeAccountId,
        );
        const selectedSavingsInternalTransferFromAccount = savingsAccounts.accounts?.find(
          (account) => account.accountId === savingsAccounts.internalTransferFromAccountId,
        );
        const selectedSavingsInternalTransferToAccount = savingsAccounts.accounts?.find(
          (account) => account.accountId === savingsAccounts.internalTransferToAccountId,
        );
        const usersActiveOrgAttributes = profile.orgList?.find(
          (org) => org.id === profile.activeOrganisationId,
        )?.attributes;
        const canUploadDocuments = usersActiveOrgAttributes?.['canUploadDocuments'] ?? false;
        const appPreferences = JSON.parse(localStorage.getItem('APP_PREFERENCES') as string) ?? {};
        // For multi-currency accounts there are several account identifiers (one per
        // payment rail / currency), so the flat identifier fields below must be resolved
        // against the currently-selected currency rather than always reading index 0.
        const selectedAccountCurrency =
          selectedAccountDetail?.accountType === 'multi-currency'
            ? (accounts.currentMultiCurrencyCode ?? '')
            : ((selectedAccountDetail?.currency as string) ?? '');
        const accountIdentifierFields = resolveAccountIdentifierFields(
          selectedAccountDetail,
          selectedAccountCurrency,
        );
        const templateModel = {
          appData: appData,
          profile: {
            ...profile,
            firstNameInitial: profile.firstName ? profile.firstName[0] : '',
            middleNameInitial: profile.middleName ? profile.middleName[0] : '',
            lastNameInitial: profile.lastName ? profile.lastName[0] : '',
          },
          cards: cards,
          cardFreezeState: cards?.currentCardDetail?.status === CardStatusType.FROZEN ? 'Unfreeze' : 'Freeze',
          savingsAccounts: savingsAccounts,
          currentSavingAccount: selectedSavingAccountDetail ?? {},
          accounts: accounts,
          depositAccountInfo: depositAccountInfo,
          alias: selectedAccountDetail?.alias ?? '',
          availableBalance: selectedAccountDetail?.availableBalance ?? '',
          accountBalance: selectedAccountDetail?.balance ?? '',
          indicativeBalance: selectedAccountDetail?.indicativeBalance ?? '',
          ledgerBalance: selectedAccountDetail?.ledgerBalance ?? '',
          ledgerBalanceIncludingPendingExchange:
            selectedAccountDetail?.ledgerBalanceIncludingPendingExchange ?? '',
          pendingExchangeNetTotal: selectedAccountDetail?.scheduledExchanges?.netTotal ?? '',
          pendingExchangeCount: selectedAccountDetail?.scheduledExchanges?.totalBooked ?? '',
          pendingTransactionNetTotal: selectedAccountDetail?.scheduledPayments?.netTotal ?? '',
          pendingTransactionCount: selectedAccountDetail?.scheduledPayments?.totalBooked ?? '',
          currency: selectedAccountCurrency,
          accountCurrency: selectedAccountDetail?.currency ?? '',
          country: selectedAccountDetail?.country ?? '', // TODO: needs deprecating as its badly named - accountCountry should be used.
          accountCountry: selectedAccountDetail?.country ?? '',
          sortCode: addDashesToSortCode(accountIdentifierFields.sortCode),
          accountNumber: accountIdentifierFields.accountNumber,
          iban: accountIdentifierFields.iban,
          bic: accountIdentifierFields.bic,
          achAccountNumber: accountIdentifierFields.achAccountNumber,
          achNumber: accountIdentifierFields.achNumber,
          abaAccountNumber: accountIdentifierFields.abaAccountNumber,
          abaNumber: accountIdentifierFields.abaNumber,
          accountProviderExtraInfo: accountIdentifierFields.providerExtraInfo,
          ledgerNumber: accountIdentifierFields.ledgerNumber,
          bsbCode: accountIdentifierFields.bsbCode,
          ifsc: accountIdentifierFields.ifsc,
          clabe: accountIdentifierFields.clabe,
          bankCode: accountIdentifierFields.bankCode,
          branchCode: accountIdentifierFields.branchCode,
          rtnCanada: accountIdentifierFields.rtnCanada,
          accountCreatedDate: selectedAccountDetail?.accountCreatedDate ?? '',
          customerName: selectedAccountDetail?.customerName ?? '',
          selectedAccountCustomerName: selectedAccountDetail?.customerName ?? '',
          selectedAccountName: selectedAccountDetail?.name ?? '',
          selectedAccountStatus: `${selectedAccountDetail?.status
            ?.charAt(0)
            ?.toUpperCase()}${selectedAccountDetail?.status?.slice(1)}`,
          currencyFullName:
            currencies?.find(
              (currency: Currency) => currency.currency_code === selectedAccountDetail?.currency,
            )?.currency_name ?? 'Unknown Currency',
          selectedBeneficiary,
          paymentRequest,
          baseFormStateData: baseFormStateData$,
          beneficiaries: beneficiaries,
          beneficiaryDetails: flattenObject(addBeneficiaryForm ?? editBeneficiaryForm),
          copResponse: copResponse,
          notification: notification,
          hasUnreadNotifications: hasUnreadNotifications(notification?.notificationList),
          pageContext: pageContext.pageContextData,
          formSubmissionFormData,
          userPendingKycForm: kycFormData,
          edituserProfileUpdateForm: profileFormData,
          intlBeneficiaryAdd,
          countryName:
            countries?.find((country: Country) => country.code === selectedAccountDetail?.country)?.name ??
            'Unknown Country',
          organisationData: organisationData,
          customQuestionFormData,
          beneficiaryDetail: selectedBeneficiaryFromBeneficiaryZone$,
          paymentRequestSummary: paymentRequestSummary$,
          profileBankStatementFormState,
          bulkPaymentSummary: bulkPaymentSummary,
          urlParamValue,
          transaction,
          paymentSummaryCounters,
          deviceInfo,
          isMobileApp: localStorage.getItem('APP_PREFERENCES') !== null,
          APP_PREFERENCES: appPreferences,
          rfi: {
            hasOpen: selectedAccountDetail?.attributes?.['rfiOpen'] === 'true',
            lastRfiId: selectedAccountDetail?.attributes?.['lastRfiId'] || undefined,
          },
          currentCardIsOnXPay: accountService?.isCardOnXPaySubject?.getValue(),
          sepaFormState,
          isTablet: isTablet,
          beneficiaryForm: beneficiaryForm$?.value ?? {},
          onboardingForm: onboardingFlow$ ?? {},
          userProfileUpdateForm: userProfileUpdateForm ?? {},
          openNewAccountForm: openNewAccountForm$ ?? {},
          maturityDecisionForm: maturityDecisionForm$ ?? {},
          nominatedAccountStatus,
          nominatedAccount: nominatedAccountDetails ?? {},
          allFormValues: allFormValues$ ?? {}, // to be used in template to access all forms values at once from workflow forms service's forms signal
          totalWealth,
          selectedSavingsInternalTransferFromAccount,
          selectedSavingsInternalTransferToAccount,
          accountRequests,
          cancellablePayments,
          apiLoadingState,
          pispInstitution,
          isGocardlessCustomPaymentPagesEnabled: isGocardlessCustomPaymentPagesEnabled,
          pispDepositInitiateResponse: pispDepositInitiateResponse,
          canUploadDocuments: canUploadDocuments ?? false,
          pendingJointAccountRequests: pendingJointAccountRequests,
          spendingBudget: (selectedAccountDetail as any)?.spendingBudget as SpendingBudget | undefined,
          recurringPayments,
        };
        return templateModel;
      },
    ),
  );
}

/* For Custom Navbar html templates. Just needs icons for the {{svgIcon}} handlebar helper */
export function buildNavBarHandleBarModel(
  iconCacheService: IconCacheService,
  store: Store,
  notificationService: NotificationService,
): Observable<object> {
  const nominatedAccountStatus$ = refreshNominatedAccountStatus(store);
  const accounts$ = store.select(AccountState.getAccounts);
  const savingsAccounts$ = store.select(SavingsAccountState.getAccounts);
  const unreadNotificationsCount$ = from(notificationService.getUnreadNotificationsCount());
  return combineLatest([
    iconCacheService.iconLoaded$,
    nominatedAccountStatus$,
    accounts$,
    savingsAccounts$,
    unreadNotificationsCount$,
  ]).pipe(
    map(
      ([
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        iconLoaded,
        nominatedAccountStatus,
        accounts,
        savingsAccounts,
        unreadNotificationsCount,
      ]) => {
        return {
          nominatedAccountStatus,
          accounts,
          savingsAccounts,
          hasUnreadNotifications: unreadNotificationsCount > 0,
        };
      },
    ),
  );
}

const formSubmissionFormData = (store: Store) => {
  const formData$ = store.select(FormActionState.getFormActionState).pipe(
    map((formActionStateData) => {
      return formActionStateData.response?.formData;
    }),
  );
  return formData$;
};

const paymentRequestData = (store: Store): Observable<any> => {
  return store
    .select(FormActionState.getFormActionStateWithId(FormActionTypeEnum.INTL_TRANSFER_RESPONSE))
    .pipe(
      switchMap((intlFormActionStateData) => {
        if (intlFormActionStateData?.formData) {
          return of(intlFormActionStateData.formData);
        } else {
          // If the intl select is empty, use the general form Action state for domestic.
          return store.select(FormActionState.getFormActionState).pipe(
            map((formActionStateData) => {
              return formActionStateData.response?.formData;
            }),
          );
        }
      }),
    );
};

const baseFormStateDataData = (store: Store): Observable<any> => {
  return store.select(FormActionState.getFormActionState).pipe(
    map((formActionStateData) => {
      return formActionStateData.response?.formData;
    }),
  );
};

const formActionStateValueWithID = (store: Store, formId: FormActionTypeEnum | FormID) => {
  return store.select(FormActionState.getFormActionStateWithId(formId)).pipe(
    map((formActionStateData) => {
      return formActionStateData?.formData;
    }),
  );
};

const getBeneficiaryDetails = (store: Store): Observable<GetBeneficiaryResponse | object> => {
  const selectedBeneficiary$ = combineLatest([
    formSubmissionFormData(store),
    store.select(BeneficiaryState.getBeneficiary),
  ]).pipe(
    map(([formActionStateData, beneficiaryList]) => {
      const beneficiaryId = formActionStateData?.['bid'];
      return beneficiaryList?.items?.find((item) => item.id === beneficiaryId) ?? {};
    }),
  );
  return selectedBeneficiary$;
};

// This is a basic regex check with a size limit to avoid performance issues on large templates
// and ReDOS attacks
function safeRegexPresentCheck(pattern: RegExp, text: string, maxSize = 18000): boolean {
  if (text.length > maxSize) {
    console.warn(
      `Template size ${text.length} exceeds maxSize of ${maxSize}. Skipping regex check to avoid performance issues.`,
    );
    return false;
  }
  return pattern.test(text);
}

export function findAndRefreshRequiredData(
  store: Store,
  template: string,
  attributes?: UiZoneWidgetAttributeConfig,
  isSkeletonPreview?: boolean,
): void {
  const profilePresent = /profile./.exec(template);
  const cardsPresent = /cards./.exec(template);
  const accountsPresent = safeRegexPresentCheck(
    /{{(?:[^}]*\b(?:accounts\.|(?<![.\w])(availableBalance|accountBalance|currency|sortCode|accountNumber|iban|ledgerNumber|bic)(?![a-zA-Z]))[^}]*?)}}|^accounts\./gm,
    template,
  );
  const savingsAccountsPresent = /savingsAccounts|currentSavingAccount./.exec(template);
  const currentSavingAccountsPresent = /currentSavingAccount./.exec(template);
  const savingProductsPresent = /getSavingProduct/.exec(template);
  const savingProductsForRenewalPresent =
    /getSavingProduct/.exec(template) && /renewalProduct/.exec(template);
  const beneficiariesPresent = /beneficiaries./.exec(template);
  const selectedBeneficiaryPresent = /selectedBeneficiary./.exec(template);
  const notificationPresent = /notification.*\.[A-Za-z]*/.exec(template);
  const organisationDataPresent = /organisationData.*\.[A-Za-z]*/.exec(template);

  const paymentSummaryCountersPresent = /paymentSummaryCounters/.exec(template);
  const depositAccountTotalInterestPresent = /depositAccountInfo\.totalInterest\.*/.exec(template);
  const depositAccountTotalBalanceWithInterestPresent =
    /depositAccountInfo\.totalBalanceWithInterest\.*/.exec(template);
  const depositAccountInfoPresent = /depositAccountInfo/.exec(template);
  const nominatedAccountDetails = /nominatedAccount/.exec(template);
  const fetchTotalWealth = /fetchTotalWealth/.exec(template);
  const accountRequestsPresent = /accountRequests/.exec(template);
  const cancellablePaymentsPresent = /cancellablePayments/.exec(template);
  const jointAccountRequestsPresent =
    /jointAccountRequests/.exec(template) || /pendingJointAccountRequests/.exec(template);

  if (jointAccountRequestsPresent) {
    getPendingJointAccountRequests(store);
  }
  const spendingBudgetPresent = /spendingBudget/.exec(template);

  if (cancellablePaymentsPresent) {
    const paymentCancellationSettings = store.selectSnapshot(ProjectSettingsState.getProjectSettings)
      ?.projectConfig?.paymentCancellationSettings;
    if (paymentCancellationSettings?.isPaymentCancellationEnabled) {
      store.dispatch(new GetCancellablePaymentsList());
    }
  }

  if (nominatedAccountDetails) {
    fetchNominatedAccountDetails(store);
  }

  if (notificationPresent) {
    dispatchWithOptionalDelay(
      store,
      SetSelectedNotification,
      attributes?.widgetProperties?.['skeletonPreviewData'] ?? selectedNotificationSkeleton,
      0,
      isSkeletonPreview,
    ); // Set skeleton template first
    store.dispatch(new ProcessNotification());
  }

  if (profilePresent) {
    const profile$ = store.select(ProfileState.getProfile);
    profile$
      .pipe(
        first(),
        map((profileData) => {
          if (!profileData?.firstName) store.dispatch(new GetProfile()).subscribe();
        }),
      )
      .subscribe();
  }

  if (accountsPresent || spendingBudgetPresent) {
    const accountContextStatusList = attributes?.['accountContextList']?.map(
      (item: { id: AccountStatusType }) => item.id,
    ) ?? [AccountStatusType.active];

    store.dispatch(new GetAccount(1, 50, accountContextStatusList));
  }

  if (savingsAccountsPresent) {
    const accountContextStatusList = attributes?.['accountContextList']?.map(
      (item: { id: AccountStatusType }) => item.id,
    ) ?? [AccountStatusType.active];

    const provider = store.selectSnapshot(ProjectSettingsState.getProjectServiceProvider);
    const limit = getProviderSpecificPageLimitForSavingsAccounts(provider);
    const isApiLoading = store.selectSnapshot(
      ApiLoadingState.getApiState(ApiActions.getSavingsAccounts),
    ).isLoading;
    if (!isApiLoading) {
      store.dispatch(new GetSavingAccount(1, limit, accountContextStatusList));
    }
  }

  if (currentSavingAccountsPresent) {
    dispatchActiveSavingsAccountDetailsFetch(store);
  }

  if (savingProductsPresent && !savingProductsForRenewalPresent) {
    const provider = store.selectSnapshot(ProjectSettingsState.getProjectServiceProvider);
    const isSavingsProvider = isASavingsProvider(provider);
    const availableProduct = store.selectSnapshot(SavingsProductsState.getAvailableProducts);
    if (!availableProduct?.length && isSavingsProvider) {
      store.dispatch(new GetAvailableSavingsProducts());
    }
  }

  if (savingProductsForRenewalPresent) {
    const provider = store.selectSnapshot(ProjectSettingsState.getProjectServiceProvider);
    const isSavingsProvider = isASavingsProvider(provider);
    const availableProductsForRenewal = store.selectSnapshot(
      SavingsProductsState.getAvailableProductsForRenewal,
    );
    if (!availableProductsForRenewal?.length && isSavingsProvider) {
      store.dispatch(new GetAvailableSavingsProducts({ page: 1, limit: 20, category: 'renewal' }));
    }
  }

  if (depositAccountTotalInterestPresent) {
    store.dispatch(new GetDepositAccountInfo([DepositDetailsType.INTEREST]));
  }

  if (depositAccountTotalBalanceWithInterestPresent) {
    store.dispatch(new GetDepositAccountInfo([DepositDetailsType.BALANCE_WITH_INTEREST]));
  }

  // Check if there's a general depositAccountInfo reference but no specific balance or interest references
  if (depositAccountInfoPresent && !depositAccountTotalInterestPresent) {
    console.log('depositAccountInfoPresent but no specific fields');
    store.dispatch(new GetDepositAccountInfo([DepositDetailsType.DEPOSIT_DETAILS]));
  }

  if (cardsPresent) {
    dispatchWithOptionalDelay<CardTransactionDetail>(
      store,
      SetCurrentCardTransactionDetail,
      attributes?.widgetProperties?.['skeletonPreviewData'] ?? currentCardTransactionDetailSkeleton,
      0,
      isSkeletonPreview,
    ); // Set skeleton template first
    store.dispatch(new GetCards());
  }

  if (beneficiariesPresent) {
    store.dispatch(new GetBeneficiary());
  }

  if (selectedBeneficiaryPresent) {
    const formData = store.selectSnapshot(FormActionState.getFormActionState)?.response?.formData;
    if (formData.bid) store.dispatch(new GetBeneficiary(1, 25, formData.name, true, formData.currency));
  }

  if (organisationDataPresent) {
    store.dispatch(new GetProfile());
    const profile$ = store.select(ProfileState.getProfile);
    profile$
      .pipe(
        take(1),
        map((profileData) => {
          if (profileData.activeOrganisationId) {
            store.dispatch(new GetOrganisation(profileData?.activeOrganisationId ?? ''));
          }
        }),
      )
      .subscribe();
  }

  if (paymentSummaryCountersPresent) {
    //find all properties after the base 'paymentSummaryCounters' handlebar
    const regex = /{{\s*paymentSummaryCounters\.(\w+)\s*}}/g;
    const properties = [...template.matchAll(regex)].map((match) => match[1]);
    store.dispatch(new GetPaymentSummaryCounters(properties));
  }

  if (fetchTotalWealth) {
    const regex = /fetchTotalWealth\.([A-Z]+)/g;

    // Find all matches and extract just the currency codes
    const matches = [...template.matchAll(regex)].map((match) => match[1]);
    if (matches.length > 0) {
      matches.forEach((currency) => {
        store.dispatch(new FetchTotalWealth(currency));
      });
    } else {
      const currency = store.selectSnapshot(AccountState.getCurrentMultiCurrencyCode);
      if (currency) {
        store.dispatch(new FetchTotalWealth(currency));
      }
    }
  }

  if (accountRequestsPresent) {
    store.dispatch(new GetAccountRequests());
  }
}

/**
 * Dispatches an NgRx action with optional delay, typically used to simulate skeleton loading behavior.
 */
const dispatchWithOptionalDelay = <T>(
  store: Store,
  action: any,
  value: T,
  delayTime: 0,
  isSkeletonPreview?: boolean,
) => {
  const payload = new action(value);

  if (isSkeletonPreview) {
    of(payload)
      .pipe(delay(delayTime))
      .subscribe((dispatchedAction) => {
        store.dispatch(dispatchedAction);
      });
  } else {
    store.dispatch(payload);
  }
};

function fetchFormDataById(store: Store, formActionId: FormActionTypeEnum) {
  const formData$ = store.select(FormActionState.getFormActionState).pipe(
    map((formActionStateData) => {
      return formActionStateData[formActionId]?.formData;
    }),
  );
  return formData$;
}

function getSelectedBeneficiaryDetail(store: Store) {
  // TODO Update this to not use the base FormActionState, not sure why we cant just search the beneficiary state for the selected beneficiary id instead
  const formData = store.selectSnapshot(FormActionState.getFormActionState)?.response?.formData;
  const beneficiary$ = store.select(BeneficiaryState.getBeneficiary).pipe(
    map((beneficiaryData) => {
      return (
        beneficiaryData.items?.find((v) => v.id === formData.bid || v.id === formData.id) ||
        beneficiaryData?.favoriteItems?.find((v) => v.id === formData.bid || v.id === formData.id) ||
        beneficiaryData?.recentPaidItems?.find((v) => v.id === formData.bid || v.id === formData.id)
      );
    }),
  );
  return beneficiary$;
}

function getPaymentRequestSummary(store: Store) {
  const paymentRequestSummary$ = store.select(NotificationState.getNotification).pipe(
    map((notificationState) => {
      return notificationState?.paymentRequestSummary;
    }),
  );
  return paymentRequestSummary$;
}

function getPispInstitution(store: Store, configService?: ConfigService) {
  /**
   * Only fetch and expose the selected PISP institution when the user is verified, the project uses
   * a savings provider, and the configured open-banking provider supports PISP flows.
   */
  if (
    !isUserVerifiedAndIsSavingsProductsProvider(store) ||
    !isPispInstitutionDisplayEnabled(store, configService)
  ) {
    return of(null);
  }

  return combineLatest([
    store.select(SavingsAccountState.getNominatedAccountDetails),
    store.select(SavingsAccountState.getPispSupportedInstitutions),
  ]).pipe(
    // Isolate the isEmpty flag so distinctUntilChanged only reacts when it transitions
    // from non-empty → empty (or on the very first empty emission), preventing a
    // repeated dispatch on every combineLatest tick while the list is still empty.
    distinctUntilChanged(([prevDetails, prevInstitutions], [currDetails, currInstitutions]) => {
      const wasEmpty = Array.isArray(prevInstitutions) && prevInstitutions.length === 0;
      const isEmpty = Array.isArray(currInstitutions) && currInstitutions.length === 0;
      // Also re-emit when nominatedAccountDetails changes
      return wasEmpty === isEmpty && prevDetails?.pispInstitutionId === currDetails?.pispInstitutionId;
    }),
    tap(([, pispSupportedInstitutions]) => {
      if (Array.isArray(pispSupportedInstitutions) && pispSupportedInstitutions.length === 0) {
        const country = store.selectSnapshot(ProfileState.getProfile)?.country;
        store.dispatch(new GetSupportedPispInstitutions(country));
      }
    }),
    // After the tap-driven dispatch the store will emit an updated (non-empty) institutions
    // list; switch back to the full stream so downstream sees every institution-or-details change.
    switchMap(([nominatedAccountDetails]) =>
      store.select(SavingsAccountState.getPispSupportedInstitutions).pipe(
        map((pispSupportedInstitutions) => {
          if (!Array.isArray(pispSupportedInstitutions)) return null;
          return (
            pispSupportedInstitutions.find(
              (institution) => institution.id === nominatedAccountDetails?.pispInstitutionId,
            ) ?? null
          );
        }),
      ),
    ),
  );
}

/**
 * @description: Example value: eq urlParamValue.flow 'bulk-payment'
 * urlParamValue is the key of url params
 * flow can be dynamic, that is query string key
 * 'bulk-payment' is a value of that query string
 */

const getPageUrlParam = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  return of(urlSearchParamsToJson(urlParams));
};

function refreshNominatedAccountStatus(store: Store): Observable<string> {
  const tokenExists = !!sessionStorage.getItem('token');
  const provider = store.selectSnapshot(ProjectSettingsState.getProjectServiceProvider);
  const isSavingsProvider = isASavingsProvider(provider);
  if (!tokenExists || !isSavingsProvider) return of('');
  if (isSavingsProvider) {
    return store.select(SavingsAccountState.getNominatedAccountStatus).pipe(
      map((status) => {
        if (!status.length) {
          const isLoading = store
            .selectSnapshot(ProgressBarState.getProgressBarStack)
            ?.some((item) => item.uniqueId === 'GetNominatedAccountStatus');
          if (!isLoading) {
            store.dispatch(new GetNominatedAccountStatus());
          }
        }
        return status;
      }),
    );
  }
  return of('');
}

function fetchNominatedAccountDetails(store: Store) {
  const tokenExists = !!sessionStorage.getItem('token');
  const provider = store.selectSnapshot(ProjectSettingsState.getProjectServiceProvider);
  const isSavingsProvider = isASavingsProvider(provider);
  if (!tokenExists || !isSavingsProvider) return;
  if (isSavingsProvider) {
    const nominatedAccountId = store.selectSnapshot(SavingsAccountState.getNominatedAccountId);
    if (!nominatedAccountId) store.dispatch(new GetNominatedAccountStatus());
    if (nominatedAccountId) store.dispatch(new GetNominatedAccountDetails(nominatedAccountId));
  }
}

function refreshAccountRequests(store: Store): Observable<AccountOpenRequestType[] | undefined> {
  // Use selectSnapshot to avoid infinite iterations
  const savingsAccountState = store.selectSnapshot(SavingsAccountState.getAccounts);
  if (isUserVerifiedAndIsSavingsProductsProvider(store)) {
    store.dispatch(new GetAccountRequests());
  }
  return of(savingsAccountState?.accountRequests);
}

function getAllFormsValues(store: Store, workflowFormsService: WorkflowFormsService | undefined) {
  const formState = store.select(FormActionState.getFormActionState);
  const formStateValuesFlattened = formState.pipe(map((state) => flattenFormStateValues(state)));
  const workflowForms = workflowFormsService?.observeAllFormsValuesFlattened() ?? of({});
  return combineLatest([workflowForms, formStateValuesFlattened]).pipe(
    map(([workflowFormsValues, formStateValuesFlattened]) => ({
      ...workflowFormsValues,
      ...formStateValuesFlattened,
    })),
  );
}
